import { useState, useContext } from 'react';
import { ShiftContext } from '../contexts/ShiftContext';
import { Lock, Unlock, Banknote, ClipboardList, X, AlertCircle } from 'lucide-react';

export default function RegisterShift() {
  const { activeSession, openSession, closeSession, loading } = useContext(ShiftContext);
  const [openingCash, setOpeningCash] = useState('0');
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (loading) return null;

  const handleOpen = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);
    try {
      await openSession(openingCash);
    } catch (err) {
      setError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = async (e) => {
    e.preventDefault();
    if (!actualCash) return setError('Please enter actual cash in drawer');
    setError('');
    setIsProcessing(true);
    try {
      await closeSession(actualCash, notes);
    } catch (err) {
      setError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!activeSession) {
    return (
      <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md z-[100] flex items-center justify-center p-4">
        <div className="bg-theme-card w-full max-w-md rounded-2xl shadow-2xl border border-theme overflow-hidden animate-fade-in">
          <div className="bg-brand/10 p-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-brand/20 rounded-full flex items-center justify-center text-brand mb-4">
              <Lock size={32} />
            </div>
            <h2 className="text-2xl font-black text-theme-main uppercase tracking-tight">Register is Closed</h2>
            <p className="text-theme-muted text-sm mt-2">Please open a new session to start making sales.</p>
          </div>
          
          <form onSubmit={handleOpen} className="p-8 space-y-6">
            {error && (
              <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl flex items-center gap-3 text-sm font-bold border border-rose-500/20">
                <AlertCircle size={18} /> {error}
              </div>
            )}
            
            <div>
              <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Opening Cash Amount</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-theme-muted font-bold">$</div>
                <input 
                  required
                  type="number"
                  step="0.01"
                  value={openingCash}
                  onChange={e => setOpeningCash(e.target.value)}
                  className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-8 pr-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-black text-lg"
                  placeholder="0.00"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isProcessing}
              className="w-full bg-brand hover:bg-brand-light text-white py-4 rounded-xl font-black transition-all shadow-lg flex items-center justify-center gap-2 uppercase tracking-widest disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : <><Unlock size={20} /> Open Register</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return null; // Active session exists, don't show the "Open" UI
}

export function CloseShiftModal({ onClose }) {
  const { activeSession, closeSession } = useContext(ShiftContext);
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClose = async (e) => {
    e.preventDefault();
    if (!actualCash) return setError('Please enter actual cash in drawer');
    setError('');
    setIsProcessing(true);
    try {
      await closeSession(actualCash, notes);
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-theme-card w-full max-w-lg rounded-2xl shadow-2xl border border-theme overflow-hidden">
        <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
          <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Close Register Session</h3>
          <button onClick={onClose} className="text-theme-muted hover:text-theme-main transition-colors"><X size={24} /></button>
        </div>

        <form onSubmit={handleClose} className="p-8 space-y-6">
          {error && (
            <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl flex items-center gap-3 text-sm font-bold border border-rose-500/20">
              <AlertCircle size={18} /> {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-6 bg-theme-main/20 p-6 rounded-2xl border border-theme">
            <div>
              <div className="text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Opened At</div>
              <div className="text-theme-main font-bold text-sm">{new Date(activeSession.openedAt).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Opening Cash</div>
              <div className="text-theme-main font-bold text-sm">${activeSession.openingCash.toFixed(2)}</div>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2 flex items-center gap-2">
              <Banknote size={14} /> Actual Cash in Drawer
            </label>
            <input 
              required
              type="number"
              step="0.01"
              value={actualCash}
              onChange={e => setActualCash(e.target.value)}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-black text-lg"
              placeholder="Count your cash..."
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2 flex items-center gap-2">
              <ClipboardList size={14} /> Notes / Discrepancy Reason
            </label>
            <textarea 
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 px-4 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all h-24 resize-none"
              placeholder="Any notes about the shift..."
            ></textarea>
          </div>

          <div className="flex gap-4">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 bg-theme-main text-theme-main border border-theme py-4 rounded-xl font-black transition-all hover:bg-theme-main/80 uppercase tracking-widest"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isProcessing}
              className="flex-2 bg-brand hover:bg-brand-light text-white py-4 px-8 rounded-xl font-black transition-all shadow-lg flex items-center justify-center gap-2 uppercase tracking-widest disabled:opacity-50"
            >
              {isProcessing ? 'Closing...' : 'Close & End Shift'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
